import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

/* ---------------- FIELD OPTIONS ---------------- */

const FIELD_OPTIONS = {
  Company: ["Is", "Contains"],
  "Primary Phone": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Street Address": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  City: ["Is", "Contains", "Is Empty", "Is Not Empty"],
  State: ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
  "Zip Code": ["Is", "Contains", "Starts With", "Is Empty", "Is Not Empty"],
  "Company Tags": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
  Website: ["Is Empty", "Is Not Empty"],
  "Tax Record Sent": [
    "On",
    "Before",
    "After",
    "Between",
    "In Last X Days",
    "Longer Than X Days",
    "Is Empty",
    "Is Not Empty",
  ],
  "Last Contacted": [
    "On",
    "Before",
    "After",
    "Between",
    "In Last X Days",
    "Longer Than X Days",
    "Is Empty",
    "Is Not Empty",
  ],
  "Property Name": ["Is", "Contains", "Is Not", "Is Any", "Is All"],
  "Own X Amount of Properties": [
    "Is",
    "Greater Than",
    "Less Than",
    "Between",
    "Is Empty",
    "Is Not Empty",
  ],
};


/* --------- FIELDS THAT USE DROPDOWN --------- */

const DROPDOWN_FIELDS = {
  Company: "companies",
  "Company Tags": "company_tags",
  "Property Name": "properties_unique",
};


/* ---------------- JOIN OPTIONS ---------------- */

const JOIN_OPTIONS = [
  { label: "AND", value: "AND" },
  { label: "OR", value: "OR" },
];

/* 🔥 REQUIRED for react-select inside modal/sidebar */
const selectPortalStyles = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999   // Very high z-index
  }),
  menu: (base) => ({
    ...base,
    zIndex: 999999,
  }),
  control: (base) => ({
    ...base,
    minHeight: "38px",
  }),
};

const DATE_FIELDS = ["Tax Record Sent", "Last Contacted"];
const NUMERIC_FIELDS = ["Own X Amount of Properties"];

const PREFIX_MAP = {
  "Tax Record Sent": "tax_record_",
  "Last Contacted": "last_contact_",
  "Own X Amount of Properties": "property_",
};

function AdvanceCompanyFilterData({ filterData, onSetFilterData, isSidebarOpen }) {
  const [config] = useAuth();

  const [groups, setGroups] = useState(
    filterData && filterData.length > 0
      ? filterData
      : [
        {
          id: Date.now(),
          condition: "AND",
          filters: [{ join: null, field: "", operator: "", value: "" }],
        },
      ]
  );

  const [dropdownOptions, setDropdownOptions] = useState({});
  const [showGroupSelector, setShowGroupSelector] = useState(false);

  // ✅ Initialize from parent data when sidebar opens
  useEffect(() => {
    if (isSidebarOpen && filterData && filterData.length > 0) {
      setGroups(filterData);

      // Pre-fetch dropdown options for existing fields
      filterData.forEach(group => {
        group.filters.forEach(filter => {
          if (filter.field && DROPDOWN_FIELDS[filter.field] && !dropdownOptions[filter.field]) {
            fetchDropdownOptions(filter.field).then((opts) =>
              setDropdownOptions((prev) => ({ ...prev, [filter.field]: opts }))
            );
          }
        });
      });
    }
  }, [isSidebarOpen]);

  /* ---------------- UPDATE STATE ---------------- */

  const updateState = (updated) => {
    setGroups(updated);
    onSetFilterData(updated); // ✅ Always sync to parent
  };

  /* ---------------- API FETCHER ---------------- */

  const fetchDropdownOptions = async (field, search = "") => {
    const list = DROPDOWN_FIELDS[field];
    if (!list) return [];

    try {
      const res = await axios.get(
        `${BASE_URL}/get-datalisting?list=${list}&search=${search}`,
        config
      );

      let options = [];

      if (list === "companies") {
        options = res?.data?.data?.company_list?.map((el) => ({
          label: el.company_name,
          value: el.id,
        })) || [];
      }
      if (list === "company_tags") {
        options =
          res?.data?.data?.company_tags?.map((el) => ({
            label: el.tag_name,
            value: el.tag_name,
          })) || [];
      }

      if (list === "properties_unique") {
        options =
          res?.data?.data?.properties?.map((el) => ({
            label: el.property_name,
            value: el.property_name,
          })) || [];
      }

      return options;
    } catch (err) {
      if (err.response?.data?.message !== "Too Many Attempts.") {
        NotificationManager.error(err.response?.data?.message);
      }
      return [];
    }
  };


  /* ---------------- ROW HANDLERS ---------------- */

  const addFilterRow = (groupIndex) => {
    const updated = [...groups];
    updated[groupIndex].filters.push({
      join: "AND",
      field: "",
      operator: "",
      value: "",
    });
    updateState(updated);
  };

  const updateRow = (groupIndex, filterIndex, key, value) => {
    const updated = [...groups];

    if (key === "field") {
      const newField = value;
      updated[groupIndex].filters[filterIndex].field = newField;
      updated[groupIndex].filters[filterIndex].operator = "";
      const isDateField = DATE_FIELDS.includes(newField);
      const isNumericField = NUMERIC_FIELDS.includes(newField);
      if (isDateField || isNumericField) {
        const prefix = PREFIX_MAP[newField];
        updated[groupIndex].filters[filterIndex].value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else {
        updated[groupIndex].filters[filterIndex].value = "";
      }
      if (DROPDOWN_FIELDS[newField] && !dropdownOptions[newField]) {
        fetchDropdownOptions(newField).then((opts) =>
          setDropdownOptions((prev) => ({ ...prev, [newField]: opts }))
        );
      }
    } else if (key === "operator") {
      const newOp = value;
      updated[groupIndex].filters[filterIndex].operator = newOp;
      const isMultiOp = ["Is Any", "Is All"].includes(newOp);
      const isDateField = DATE_FIELDS.includes(updated[groupIndex].filters[filterIndex].field);
      const isNumericField = NUMERIC_FIELDS.includes(updated[groupIndex].filters[filterIndex].field);
      if (isDateField || isNumericField) {
        const prefix = PREFIX_MAP[updated[groupIndex].filters[filterIndex].field];
        updated[groupIndex].filters[filterIndex].value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else {
        updated[groupIndex].filters[filterIndex].value = isMultiOp ? [] : "";
      }
    } else {
      updated[groupIndex].filters[filterIndex][key] = value;
    }

    updateState(updated);
  };

  const handleFilterChange = (groupIndex, filterIndex, subKey, subValue) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    filter.value = { ...filter.value || {}, [subKey]: subValue };
    updateState(updated);
  };

  const removeRow = (groupIndex, filterIndex) => {
    const updated = [...groups];

    if (updated[groupIndex].filters.length === 1) {
      if (updated.length > 1) {
        updated.splice(groupIndex, 1);
      } else {
        updated[groupIndex].filters[0] = { join: null, field: "", operator: "", value: "" };
      }
    } else {
      updated[groupIndex].filters.splice(filterIndex, 1);
    }

    updateState(updated);
  };

  const createNestedGroup = (join) => {
    updateState([
      ...groups,
      {
        id: Date.now(),
        condition: join,
        filters: [{ join: null, field: "", operator: "", value: "" }],
      },
    ]);
    setShowGroupSelector(false);
  };

  /* ---------------- RENDER ---------------- */

  return (
    <div className="space-y-6 text-black">
      {groups.map((group, groupIndex) => (
        <div key={group.id} className="bg-white space-y-2">
          {groupIndex > 0 && (
            <div className="text-sm font-medium mb-2 text-gray-600">
              {group.condition} Group
            </div>
          )}

          {group.filters.map((filter, index) => (
            <div key={index}>
              {/* FILTER ROW - Field, Operator, Value on same line */}
              <div className="flex items-center gap-2">
                {/* JOIN SELECTOR */}
                {index > 0 && (
                  <Select
                    className="w-24 dark-H body-N"
                    placeholder="Join"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                    options={JOIN_OPTIONS}
                    value={
                      filter.join
                        ? { label: filter.join, value: filter.join }
                        : { label: "AND", value: "AND" }
                    }
                    onChange={(e) => updateRow(groupIndex, index, "join", e.value)}
                  />
                )}

                {/* FIELD */}
                <Select
                  className="flex-1 dark-H body-N"
                  placeholder="Select Field"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                  options={Object.keys(FIELD_OPTIONS).map((f) => ({
                    label: f,
                    value: f,
                  }))}
                  value={
                    filter.field
                      ? { label: filter.field, value: filter.field }
                      : null
                  }
                  onChange={(e) => updateRow(groupIndex, index, "field", e.value)}
                />

                {/* OPERATOR */}
                <Select
                  className="flex-1 dark-H body-N"
                  placeholder="Condition"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                  isDisabled={!filter.field}
                  options={
                    filter.field
                      ? FIELD_OPTIONS[filter.field].map((c) => ({
                        label: c,
                        value: c,
                      }))
                      : []
                  }
                  value={
                    filter.operator
                      ? { label: filter.operator, value: filter.operator }
                      : null
                  }
                  onChange={(e) => updateRow(groupIndex, index, "operator", e.value)}
                />

                {/* VALUE - Only for non-date/non-numeric fields */}
                {filter.operator && !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                  !DATE_FIELDS.includes(filter.field) && !NUMERIC_FIELDS.includes(filter.field) && (
                    DROPDOWN_FIELDS[filter.field] && filter.operator !== "Contains" ? (
                      (() => {
                        const isMulti = ["Is Any", "Is All"].includes(filter.operator);
                        const fieldOptions = dropdownOptions[filter.field] || [];
                        const selectedValue = filter.value
                          ? isMulti
                            ? fieldOptions.filter((opt) => filter.value.includes(opt.value))
                            : fieldOptions.find((opt) => opt.value === filter.value)
                          : isMulti
                            ? []
                            : null;
                        return (
                          <AsyncSelect
                            className="flex-1 dark-H body-N"
                            placeholder="Select Value"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={selectPortalStyles}
                            isMulti={isMulti}
                            defaultOptions={fieldOptions}
                            loadOptions={async (inputValue) => {
                              const opts = await fetchDropdownOptions(filter.field, inputValue);
                              setDropdownOptions((prev) => {
                                const existing = prev[filter.field] || [];
                                const uniqueOpts = [...existing, ...opts].reduce((acc, current) => {
                                  if (!acc.find((item) => item.value === current.value)) {
                                    acc.push(current);
                                  }
                                  return acc;
                                }, []);
                                return { ...prev, [filter.field]: uniqueOpts };
                              });
                              return opts;
                            }}
                            value={selectedValue}
                            onChange={(selected) => {
                              const newValue = isMulti
                                ? selected
                                  ? selected.map((opt) => opt.value)
                                  : []
                                : selected
                                  ? selected.value
                                  : null;
                              updateRow(groupIndex, index, "value", newValue);
                            }}
                          />
                        );
                      })()
                    ) : (
                      <input
                        className="flex-1 border border-[#f4f4f4] px-2 rounded-[8px] h-[38px] m-0 dark-H body-N outline-none shadow-[0px_0px_20px_0px_#007d880d] focus:border-[#f4f4f4] focus:ring-0"
                        placeholder="Enter Value"
                        value={filter.value || ""}
                        onChange={(e) =>
                          updateRow(groupIndex, index, "value", e.target.value)
                        }
                      />
                    )
                  )}

                {/* REMOVE BUTTON */}
                <button
                  className="text-red-500 hover:text-red-700 px-2"
                  onClick={() => removeRow(groupIndex, index)}
                  title="Remove filter"
                >
                  ✕
                </button>
              </div>

              {/* DATE RANGE - On next line when date field is selected */}
              {filter.operator &&
                !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                DATE_FIELDS.includes(filter.field) && (
                  <div className="mt-2 pl-2">
                    {(() => {
                      const prefix = PREFIX_MAP[filter.field];
                      const operator = filter.operator;
                      if (["In Last X Days", "Longer Than X Days"].includes(operator)) {
                        return (
                          <input
                            type="number"
                            className="dark-H body-N w-full"
                            placeholder="Enter number of days"
                            min={0}
                            value={filter.value?.[`${prefix}count`] || ""}
                            onChange={(e) => handleFilterChange(groupIndex, index, `${prefix}count`, e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />
                        );
                      } else if (["On", "Before", "After"].includes(operator)) {
                        return (
                          <DatePicker
                            className="dark-H body-N w-full"
                            dateFormat="MM/dd/yyyy"
                            placeholderText="MM/DD/YYYY"
                            maxDate={new Date()}
                            selected={filter.value?.[`${prefix}count`] ? moment.tz(filter.value?.[`${prefix}count`], 'America/New_York').toDate() : null}
                            onChange={(date) => {
                              const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                              handleFilterChange(groupIndex, index, `${prefix}count`, val);
                            }}
                          />
                        );
                      } else if (operator === "Between") {
                        return (
                          <div className="flex flex-row gap-2">
                            <DatePicker
                              className="dark-H body-N"
                              dateFormat="MM/dd/yyyy"
                              placeholderText="Start Date (MM/DD/YYYY)"
                              maxDate={new Date()}
                              selected={filter.value?.[`${prefix}start_count`] ? moment.tz(filter.value?.[`${prefix}start_count`], 'America/New_York').toDate() : null}
                              onChange={(date) => {
                                const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                                handleFilterChange(groupIndex, index, `${prefix}start_count`, val);
                              }}
                            />
                            <DatePicker
                              className="dark-H body-N"
                              dateFormat="MM/dd/yyyy"
                              placeholderText="End Date (MM/DD/YYYY)"
                              maxDate={new Date()}
                              selected={filter.value?.[`${prefix}end_count`] ? moment.tz(filter.value?.[`${prefix}end_count`], 'America/New_York').toDate() : null}
                              onChange={(date) => {
                                const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                                handleFilterChange(groupIndex, index, `${prefix}end_count`, val);
                              }}
                            />
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

              {/* NUMERIC RANGE - On next line when numeric field is selected */}
              {filter.operator &&
                !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                NUMERIC_FIELDS.includes(filter.field) && (
                  <div className="mt-2 pl-2">
                    {(() => {
                      const prefix = PREFIX_MAP[filter.field];
                      return (
                        <OptionalOption
                          activeTab="date"
                          type="number"
                          title="Properties"
                          onSetLastDate={(value) => handleFilterChange(groupIndex, index, `${prefix}count`, value)}
                          date={filter.value?.[`${prefix}count`] || ""}
                          onSetDateRange1={(value) =>
                            handleFilterChange(groupIndex, index, `${prefix}start_count`, value)
                          }
                          date2={filter.value?.[`${prefix}start_count`] || ""}
                          onSetDateRange2={(value) =>
                            handleFilterChange(groupIndex, index, `${prefix}end_count`, value)
                          }
                          date3={filter.value?.[`${prefix}end_count`] || ""}
                          onClear={() => {
                            handleFilterChange(groupIndex, index, `${prefix}count`, "");
                            handleFilterChange(groupIndex, index, `${prefix}start_count`, "");
                            handleFilterChange(groupIndex, index, `${prefix}end_count`, "");
                          }}
                        />
                      );
                    })()}
                  </div>
                )}
            </div>
          ))}

          {/* ADD FILTER BUTTON */}
          <button
            className="green-H head-5 mt-2"
            onClick={() => addFilterRow(groupIndex)}
          >
            + Add Filter
          </button>
        </div>
      ))}

      {/* ADD NESTED GROUP */}
      {!showGroupSelector ? (
        <button
          className="green-H head-5"
          onClick={() => setShowGroupSelector(true)}
        >
          + Add Nested Filter Group
        </button>
      ) : (
        <Select
          autoFocus
          className="dark-H body-N"
          placeholder="Select Group Type"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={selectPortalStyles}
          options={[
            { label: "AND Group", value: "AND" },
            { label: "OR Group", value: "OR" },
          ]}
          onChange={(e) => createNestedGroup(e.value)}
          onBlur={() => setShowGroupSelector(false)}
        />
      )}
    </div>
  );
}

export default AdvanceCompanyFilterData;